import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import { GridRowId, GridValidRowModel } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useState } from "react";

const useRowSelectionModel = (
  apiRef: MutableRefObject<GridApiPremium>
) => {
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);

  const [orderedScenarioSelections, setOrderedScenarioSelections] = useState<string[]>([]);

  const handelRowSelectionModelChange = async (newRowSelectionModel: GridRowId[]) => {
    rowSelectionModel.forEach((rowId: GridRowId) => {
      const row = apiRef.current.getRow(rowId);
      if (row?.type === "model") {
        if (!rowSelectionModel.find((rowId: GridRowId) => rowId === row.data.scenarioId)) {
          rowSelectionModel.push(row.data.scenarioId);
        }
      }
    });

    let scenarios: GridValidRowModel[] = newRowSelectionModel.reduce((acc: GridValidRowModel[], rowId: GridRowId) => {
      const row = apiRef.current.getRow(rowId);
      if (row?.type === "scenario") {
        acc.push(row);
      }
      return acc;
    }, []);

    let models = newRowSelectionModel.reduce((acc: GridValidRowModel[], rowId: GridRowId) => {
      const row = apiRef.current.getRow(rowId);
      if (row?.type === "model") {
        acc.push(row);

        if (!scenarios.find((scenario: GridValidRowModel) => scenario.key === row.data.scenarioId)) {
          const scenario = apiRef.current.getRow(row.data.scenarioId);
          scenarios.push(scenario);
        }
      }
      return acc;
    }, []);

    scenarios.sort((a: GridValidRowModel, b: GridValidRowModel) => b.index - a.index);

    const scenariosToRemove: string[] = [];
    const modelsToRemove: GridValidRowModel[] = [];

    // Limit selections to N scenarios based on the selection mode
    // by removing the previous selected scenarios
    const maxScenarioSelections = selectionManager.maxScenarioSelections;
    while (scenarios.length > maxScenarioSelections) {
      let scenarioToRemove = orderedScenarioSelections.shift();
      if (!scenarioToRemove) {
        scenarioToRemove = scenarios.at(-1)?.key;
      }

      // Remove any associated models from the selection
      models = models.filter((model: GridValidRowModel) => {
        if (model.data.scenarioId !== scenarioToRemove) {
          return model;
        }
        newRowSelectionModel = newRowSelectionModel.filter((rowId: GridRowId) => rowId !== model.key);
        modelsToRemove.push(model);
        return null;
      });

      scenariosToRemove.push(scenarioToRemove!);

      // Remove the scenario from the scenarios and selection arrays
      scenarios = scenarios.filter((scenario: GridValidRowModel) => scenario.key !== scenarioToRemove);
      newRowSelectionModel = newRowSelectionModel.filter((rowId: GridRowId) => rowId !== scenarioToRemove);
    }

    //console.log("scenarios: ", scenarios);
    //console.log("models: ", models);

    // Remove any models that are not in the new selection
    rowSelectionModel.forEach((rowId: GridRowId) => {
      const row = apiRef.current.getRow(rowId);
      if (row?.type === "scenario" && !scenarios.find((model: GridValidRowModel) => model.key === row.key)) {
        scenariosToRemove.push(row.key);
      }
      if (row?.type === "model" && !models.find((model: GridValidRowModel) => model.key === row.key)) {
        modelsToRemove.push(row);
      }
    });

    //console.log("scenariosToRemove: ", scenariosToRemove);
    //console.log("modelsToRemove: ", modelsToRemove);

    for (const item of modelsToRemove) {
      await selectionManager.unSelectModels(item.data.scenarioId, item.data.model.apsViewId);
    }

    for (const item of scenariosToRemove) {
      await selectionManager.unSelectScenario(item);
    }

    if (scenarios.length === 0) {
      setOrderedScenarioSelections([]);
    }
    // Keep track of the order of the selected scenarios in an FIFO queue
    const newScenarioIds = scenarios.filter((scenario: GridValidRowModel) => !orderedScenarioSelections.includes(scenario.key)).map((scenario: GridValidRowModel) => scenario.key);
    if (newScenarioIds.length > 0) {
      orderedScenarioSelections.push(...newScenarioIds);
      setOrderedScenarioSelections([...orderedScenarioSelections]);
      //console.log("orderedScenarioSelections: ", orderedScenarioSelections);
    }

    setRowSelectionModel(newRowSelectionModel);

    return { models, newRowSelectionModel };
  };

  return { handelRowSelectionModelChange, selectionModel: rowSelectionModel };
};

export default useRowSelectionModel;